import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dropdown, MenuProps, Pagination, Space, Spin, Select } from 'antd';
import s from './s.module.less';
import dayjs from 'utils/dayjs';
import { DownOutlined } from '@ant-design/icons';
import { PatientsFeedback } from 'types/common';
import Down from './down.svg';
import Up from './up.svg';
import Empty from './empty.svg';
import { getTimeStr } from 'utils/calendar';
import ProviderStore from 'store/Provider';
import ReplyFeedbackModal from '../ReplyFeedbackModal';
import { IOverviewData } from 'types/reputation';
import { getReputationOverviewData, getReputationFeedbackList } from 'api/survey';
import DataUpdatedFlagStore from 'store/DataUpdatedFlag';
import { isFreeUser } from 'utils/provider';
import { openSubscriptionModal } from 'utils/globalLayerControl';
import Star from 'components/Star';
import CheckboxSelect from 'components/CheckboxSelect';
import { getFullName } from 'utils/common';
import ReplyUpgradeTipModal from '../ReplyUpgradeTipModal';

enum ETimeFilter {
    THIS_WEEK = 'this_week',
    LAST_30_DAYS = 'last_30',
    LAST_90_DAYS = 'last_90',
}

enum EFeedbackFilter {
    OLDEST = 1,
    NEWEST = 0,
}

enum EFeedbackType {
    NORMAL = 1,
    ANONYMOUS = 2,
}

export enum ECollectType {
    COLLECTED_VIA_SURVEY_LINK = 'COLLECTED_VIA_SURVEY_LINK',
    COLLECTED_VIA_WEB_APP = 'COLLECTED_VIA_WEB_APP',
}

const collectOptions = [
    {
        label: 'Collected via web app',
        value: ECollectType.COLLECTED_VIA_WEB_APP,
    },
    {
        label: 'Collected via survey link',
        value: ECollectType.COLLECTED_VIA_SURVEY_LINK,
    },
];

const feedbackFilterOptions = [
    {
        label: 'Newest',
        value: EFeedbackFilter.NEWEST,
    },
    {
        label: 'Oldest',
        value: EFeedbackFilter.OLDEST,
    },
];

const Overview = () => {
    const [getUser] = ProviderStore.useStore();
    const navigate = useNavigate();
    const [timeFilter, setTimeFilter] = React.useState<ETimeFilter>(ETimeFilter.THIS_WEEK);
    const [feedbackFilter, setFeedbackFilter] = React.useState<EFeedbackFilter>(EFeedbackFilter.NEWEST);
    const [showReply, setShowReply] = React.useState<boolean>(false);
    const [currentFeedback, setCurrentFeedback] = React.useState<PatientsFeedback>();
    const [feedbackData, setFeedbackData] = useState<PatientsFeedback[]>();
    const [getDataUpdatedFlag] = DataUpdatedFlagStore.useStore();

    const [overviewData, setOverviewData] = useState<IOverviewData>({
        invitationSend: 0,
        averageSurveyRating: 0.0,
        feedbackReceived: 0,
        surveySendThisWeek: 0,
        surveySendTotal: 0,
        surveyDeliverySend: 0,
        preInvitationSend: 0,
        preAverageSurveyRating: 0.0,
        preFeedbackReceived: 0,
        preSurveySendTotal: 0,
        surveySendIncrRate: 0.0,
    });
    const [feedbackListPage, setFeedbackListPage] = useState(1);
    const [feedbackListPageSize, setFeedbackListPageSize] = useState(10);
    const [totalFeedback, setTotalFeedback] = useState(0);
    const patientFeedbackListUpdatedFlag = getDataUpdatedFlag('patientFeedbackListUpdated');
    const surveyDataUpdatedFlag = getDataUpdatedFlag('surveyDataUpdated');
    const [loading, setLoading] = React.useState(false);

    const isFree = isFreeUser();
    const user = getUser('data');
    const hasLoaded = !!user?.email;

    const showUpgrade = !!isFree && hasLoaded;
    const showChannelStore = !isFree && !user?.gmail && hasLoaded;
    const canReply = !showUpgrade && !showChannelStore;

    const [replyUpgradeTipModalOpen, setReplyUpgradeTipModalOpen] = React.useState(false);
    const [replyUpgradeTipModalTitle, setReplyUpgradeTipModalTitle] = React.useState('');
    const [replyUpgradeTipModalDescription, setReplyUpgradeTipModalDescription] = React.useState('');
    const [replyUpgradeTipModalButtonText, setReplyUpgradeTipModalButtonText] = React.useState('');
    const [replyUpgradeTipModalButtonCallback, setReplyUpgradeTipModalButtonCallback] = React.useState<(() => void) | null>(null);
    useEffect(() => {
        if (!canReply) {
            if (isFree) {
                setReplyUpgradeTipModalTitle('Upgrade now to unlock patient responses');
                setReplyUpgradeTipModalDescription('Replies are available with a paid plan. Upgrade to engage with patients, address feedback, and build stronger relationships.');
                setReplyUpgradeTipModalButtonText('Upgrade now');
                setReplyUpgradeTipModalButtonCallback(() => openSubscriptionModal);
            } else {
                setReplyUpgradeTipModalTitle('Now complete Practice Front to unlock patient responses');
                setReplyUpgradeTipModalDescription('');
                setReplyUpgradeTipModalButtonText('Complete now');
                setReplyUpgradeTipModalButtonCallback(() => () => navigate('/dashboard/practice-front'));
            }
        }
    }, [canReply, isFree, navigate]);

    const [collectType, setCollectType] = React.useState<ECollectType[]>([]);

    const fetchOverviewData = async (v: string) => {
        try {
            const res = await getReputationOverviewData(v);

            if (res && !res.error && res.data?.data) {
                setOverviewData(res.data.data);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const getFeedbackList = async (
        { type, order, current, size, sourceEnum }:
        { type: EFeedbackType, order?: EFeedbackFilter, current?: number, size?:number, sourceEnum?: ECollectType[] | null },
    ) => {
        try {
            setLoading(true);
            const res = await getReputationFeedbackList({
                isAnonymous: type === EFeedbackType.ANONYMOUS,
                orderAsc: order === EFeedbackFilter.OLDEST,
                size: size || feedbackListPageSize,
                current: current || feedbackListPage,
                replied: false,
                sourceEnum: !sourceEnum || sourceEnum.length === 2 || sourceEnum.length === 0 ? null : sourceEnum.join(','),
            });
            setLoading(false);

            if (res && !res.error) {
                const { records = [], total = 0 } = res.data?.data || {};
                setFeedbackData(records || []);
                setFeedbackListPage(1);
                setTotalFeedback(total);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        getFeedbackList({ type: EFeedbackType.NORMAL });
        fetchOverviewData(ETimeFilter.THIS_WEEK);
    }, []);

    useEffect(() => {
        if (patientFeedbackListUpdatedFlag) {
            getFeedbackList({ type: EFeedbackType.NORMAL });
            fetchOverviewData(timeFilter);
        }
    }, [patientFeedbackListUpdatedFlag]);

    useEffect(() => {
        fetchOverviewData(timeFilter);
    }, [surveyDataUpdatedFlag]);

    const handlePaginationChange = (page: number, pageSize: number) => {
        setFeedbackListPage(page);
        setFeedbackListPageSize(pageSize);
        getFeedbackList({ type: EFeedbackType.NORMAL, current: page, size: pageSize, order: feedbackFilter, sourceEnum: collectType });
    };

    let timeFilterLabel = '';
    if (timeFilter === ETimeFilter.THIS_WEEK) {
        timeFilterLabel = 'This week';
    }
    if (timeFilter === ETimeFilter.LAST_30_DAYS) {
        timeFilterLabel = 'Last 30 days';
    }
    if (timeFilter === ETimeFilter.LAST_90_DAYS) {
        timeFilterLabel = 'Last 90 days';
    }

    let feedbackFilterLabel = '';
    if (feedbackFilter === EFeedbackFilter.NEWEST) {
        feedbackFilterLabel = 'Newest';
    }
    if (feedbackFilter === EFeedbackFilter.OLDEST) {
        feedbackFilterLabel = 'Oldest';
    }

    const timeFilterItems: MenuProps['items'] = [
        {
            label: <span className={s.dropItem} onClick={() => { setTimeFilter(ETimeFilter.THIS_WEEK); }}>This week</span>,
            key: ETimeFilter.THIS_WEEK,
        },
        {
            label: <span className={s.dropItem} onClick={() => { setTimeFilter(ETimeFilter.LAST_30_DAYS); }}>Last 30 days</span>,
            key: ETimeFilter.LAST_30_DAYS,
        },
        {
            label: <span className={s.dropItem} onClick={() => { setTimeFilter(ETimeFilter.LAST_90_DAYS); }}>Last 90 days</span>,
            key: ETimeFilter.LAST_90_DAYS,
        },
    ];

    const isEmpty = (feedbackData && feedbackData.length === 0);

    const timeFilterChange:MenuProps['onClick'] = ({ key }) => {
        fetchOverviewData(key);
    };

    const {
        invitationSend,
        averageSurveyRating,
        feedbackReceived,
        surveySendTotal,
        preInvitationSend,
        preAverageSurveyRating,
        preFeedbackReceived,
        preSurveySendTotal,
        surveySendIncrRate,
        averageRatingIncrRate,
        feedbackReceivedIncrRate,
        invitationSendIncrRate,
    } = overviewData;

    const renderRisingArrow = (val?: number) => {
        if (!val) {
            return <span />;
        }

        if (val > 0) {
            return (
                <div className={s.up}>
                    <img src={Up} alt="" />
                    <span className={s.rate}>{val}%</span>
                </div>
            );
        }

        if (val < 0) {
            return (
                <div className={s.down}>
                    <img src={Down} alt="" />
                    <span className={s.rate}>{val}%</span>
                </div>
            );
        }
    };

    const handleChange = (value: ECollectType[]) => {
        setCollectType(value);
        getFeedbackList({ type: EFeedbackType.NORMAL, order: feedbackFilter, current: 1, sourceEnum: value });
    };

    const handleFeedbackFilterChange = (value: EFeedbackFilter) => {
        setFeedbackFilter(value);
        getFeedbackList({ type: EFeedbackType.NORMAL, order: value, current: 1, sourceEnum: collectType });
    };

    return (
        <div className={s.wrap}>
            <ReplyUpgradeTipModal
                title={replyUpgradeTipModalTitle}
                description={replyUpgradeTipModalDescription}
                buttonText={replyUpgradeTipModalButtonText}
                open={replyUpgradeTipModalOpen}
                onCancel={() => setReplyUpgradeTipModalOpen(false)}
                onOk={() => {
                    setReplyUpgradeTipModalOpen(false);
                    replyUpgradeTipModalButtonCallback?.();
                }}
            />
            <div className={s.header}>
                <div className={s.timeFilter}>
                    <Dropdown menu={{ items: timeFilterItems, onClick: timeFilterChange }} trigger={['click']}>
                        <a onClick={(e) => e.preventDefault()}>
                            <Space>
                                <span className={s.filterLabel}>{timeFilterLabel}</span>
                                <DownOutlined />
                            </Space>
                        </a>
                    </Dropdown>
                </div>
                <div className={s.rowWrap}>
                    <div className={s.rowIn}>
                        <div className={s.item}>
                            <div className={s.top}>
                                <div className={s.label}>Total survey sent</div>
                            </div>
                            <div className={s.bottom}>
                                <div className={s.left}><span className={s.val}>{surveySendTotal}</span> from {preSurveySendTotal}</div>
                                <div className={s.right}>
                                    {
                                        renderRisingArrow(surveySendIncrRate)
                                    }
                                </div>
                            </div>
                        </div>
                        <div className={s.item}>
                            <div className={s.top}>
                                <div className={s.label}>Average survey rating</div>
                            </div>
                            <div className={s.bottom}>
                                <div className={s.left}><span className={s.val}>{averageSurveyRating}</span> <span className={s.valDes}>out of 5</span> from {preAverageSurveyRating}</div>
                                <div className={s.right}>
                                    {
                                        renderRisingArrow(averageRatingIncrRate)
                                    }
                                </div>
                            </div>
                        </div>
                        <div className={s.item}>
                            <div className={s.top}>
                                <div className={s.label}>Total feedback received</div>
                            </div>
                            <div className={s.bottom}>
                                <div className={s.left}><span className={s.val}>{feedbackReceived}</span> from {preFeedbackReceived}</div>
                                <div className={s.right}>
                                    {
                                        renderRisingArrow(feedbackReceivedIncrRate)
                                    }
                                </div>
                            </div>
                        </div>
                        <div className={s.item}>
                            <div className={s.top}>
                                <div className={s.label}>Total invitation sent</div>
                            </div>
                            <div className={s.bottom}>
                                <div className={s.left}><span className={s.val}>{invitationSend}</span> from {preInvitationSend}</div>
                                <div className={s.right}>
                                    {
                                        renderRisingArrow(invitationSendIncrRate)
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={s.tips}>
                {
                    showUpgrade && (
                        <div className={s.upgrade}>
                            <div className={s.nodeLeft}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M5 2C5.55228 2 6 2.44772 6 3V4H7C7.55228 4 8 4.44772 8 5C8 5.55228 7.55228 6 7 6H6V7C6 7.55228 5.55228 8 5 8C4.44772 8 4 7.55228 4 7V6H3C2.44772 6 2 5.55228 2 5C2 4.44772 2.44772 4 3 4H4V3C4 2.44772 4.44772 2 5 2ZM5 12C5.55228 12 6 12.4477 6 13V14H7C7.55228 14 8 14.4477 8 15C8 15.5523 7.55228 16 7 16H6V17C6 17.5523 5.55228 18 5 18C4.44772 18 4 17.5523 4 17V16H3C2.44772 16 2 15.5523 2 15C2 14.4477 2.44772 14 3 14H4V13C4 12.4477 4.44772 12 5 12Z" fill="#FBBF24" />
                                    <path fillRule="evenodd" clipRule="evenodd" d="M11.9999 2C12.4537 2 12.8505 2.30548 12.9667 2.74411L14.1459 7.19893L17.4997 9.13381C17.8092 9.31241 17.9999 9.64262 17.9999 10C17.9999 10.3574 17.8092 10.6876 17.4997 10.8662L14.1459 12.8011L12.9667 17.2559C12.8505 17.6945 12.4537 18 11.9999 18C11.5462 18 11.1493 17.6945 11.0332 17.2559L9.85402 12.8011L6.50027 10.8662C6.19072 10.6876 6 10.3574 6 10C6 9.64262 6.19072 9.31241 6.50027 9.13382L9.85402 7.19893L11.0332 2.74411C11.1493 2.30548 11.5462 2 11.9999 2Z" fill="#FBBF24" />
                                </svg>
                            </div>
                            <div className={s.nodeRight}>
                                <div className={s.text}>
                                    Elevate patient communication with our &apos;Reply Feedback&apos; tool. As a premium feature, it enables timely responses to patient feedback, building stronger trust and engagement. Enhance your practice&apos;s responsiveness and patient care by upgrading your plan now.
                                </div>
                                <div
                                    onClick={() => {
                                        openSubscriptionModal();
                                    }}
                                    className={s.upText}
                                >
                                    Upgrade plan
                                </div>
                            </div>
                        </div>
                    )
                }
                {
                    showChannelStore && (
                        <div className={s.upgrade}>
                            <div className={s.nodeLeft}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M5 2C5.55228 2 6 2.44772 6 3V4H7C7.55228 4 8 4.44772 8 5C8 5.55228 7.55228 6 7 6H6V7C6 7.55228 5.55228 8 5 8C4.44772 8 4 7.55228 4 7V6H3C2.44772 6 2 5.55228 2 5C2 4.44772 2.44772 4 3 4H4V3C4 2.44772 4.44772 2 5 2ZM5 12C5.55228 12 6 12.4477 6 13V14H7C7.55228 14 8 14.4477 8 15C8 15.5523 7.55228 16 7 16H6V17C6 17.5523 5.55228 18 5 18C4.44772 18 4 17.5523 4 17V16H3C2.44772 16 2 15.5523 2 15C2 14.4477 2.44772 14 3 14H4V13C4 12.4477 4.44772 12 5 12Z" fill="#FBBF24" />
                                    <path fillRule="evenodd" clipRule="evenodd" d="M11.9999 2C12.4537 2 12.8505 2.30548 12.9667 2.74411L14.1459 7.19893L17.4997 9.13381C17.8092 9.31241 17.9999 9.64262 17.9999 10C17.9999 10.3574 17.8092 10.6876 17.4997 10.8662L14.1459 12.8011L12.9667 17.2559C12.8505 17.6945 12.4537 18 11.9999 18C11.5462 18 11.1493 17.6945 11.0332 17.2559L9.85402 12.8011L6.50027 10.8662C6.19072 10.6876 6 10.3574 6 10C6 9.64262 6.19072 9.31241 6.50027 9.13382L9.85402 7.19893L11.0332 2.74411C11.1493 2.30548 11.5462 2 11.9999 2Z" fill="#FBBF24" />
                                </svg>
                            </div>
                            <div className={s.nodeRight}>
                                <div className={s.text}>
                                    To reply to feedback in Reputation Management, you must be listed on at least one channel. Visit the Channel Store to select and add your preferred channels.
                                </div>
                                <div
                                    onClick={() => {
                                        navigate('/dashboard/channel');
                                    }}
                                    className={s.upText}
                                >
                                    Browse channel store
                                </div>
                            </div>
                        </div>
                    )
                }
            </div>
            <div className={s.feedback}>
                <div className={s.feedbackHeader}>
                    <h2>Feedbacks need reply ({totalFeedback})</h2>
                    <div className={s.feedbackFilter}>
                        <CheckboxSelect
                            placeholder="Select collection methods"
                            style={{ width: '250px' }}
                            value={collectType as string[]}
                            options={collectOptions}
                            onChange={(values: string[]) => handleChange(values as ECollectType[])}
                        />
                        <Select
                            value={feedbackFilter}
                            style={{ width: '120px' }}
                            onChange={handleFeedbackFilterChange}
                            options={feedbackFilterOptions}
                        />
                    </div>
                </div>
                <Spin className={s.feedbackContent} spinning={loading}>
                    {
                        isEmpty && (
                            <div className={s.emtpyWrap}>
                                <div className={s.emptyTitle}>Great job! You’re all caught up.</div>
                                <div className={s.emptyDes}>No feedbacks need to be replied.</div>
                                <div className={s.emptyImg}>
                                    <img src={Empty} alt="" />
                                </div>
                            </div>
                        )
                    }
                    {
                        feedbackData?.map((feedback:PatientsFeedback) => {
                            const hasEmail = !!(feedback.email);
                            // const replyButtonEnabled = canReply || hasEmail || feedback.isNeedReply;
                            const replyButtonEnabled = hasEmail || feedback.isNeedReply;
                            return (
                                <div className={s.feedbackItem} key={`${feedback.id}`}>
                                    <div className={s.left}>
                                        <div className={s.info}>
                                            <div className={s.nameAndScore}>
                                                <div className={s.name}>{getFullName(feedback.firstName, feedback.lastName)} {feedback.source && <span className={s.source}>{`| ${feedback.source}`}</span>}</div>
                                            </div>
                                            {
                                                (feedback.email || feedback.phoneNumber) && (
                                                    <div className={s.emailAndPhone}>
                                                        {
                                                            feedback.email && (
                                                                <div className={s.email}>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                        <path d="M2 5.33398L7.2604 8.84092C7.70827 9.1395 8.29173 9.1395 8.7396 8.84092L14 5.33398M3.33333 12.6673H12.6667C13.403 12.6673 14 12.0704 14 11.334V4.66732C14 3.93094 13.403 3.33398 12.6667 3.33398H3.33333C2.59695 3.33398 2 3.93094 2 4.66732V11.334C2 12.0704 2.59695 12.6673 3.33333 12.6673Z" stroke="#9CA3AF" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                                    </svg>
                                                                    {feedback.email}
                                                                </div>
                                                            )
                                                        }
                                                        {
                                                            feedback.phoneNumber && (
                                                                <div className={s.phone}>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                        <path d="M6.15195 2.45585L5.51949 2.66667V2.66667L6.15195 2.45585ZM7.15049 5.45147L7.78295 5.24066L7.78295 5.24066L7.15049 5.45147ZM5.31134 7.011L5.0132 6.41471C4.69317 6.57473 4.55652 6.95883 4.70359 7.28501L5.31134 7.011ZM8.989 10.6887L8.71499 11.2964C9.04117 11.4435 9.42527 11.3068 9.58529 10.9868L8.989 10.6887ZM9.74142 9.18382L9.14514 8.88568L9.14514 8.88568L9.74142 9.18382ZM10.5485 8.84951L10.7593 8.21705L10.5485 8.84951ZM13.5442 9.84805L13.3333 10.4805L13.5442 9.84805ZM2 4.00065V3.33398C1.82318 3.33398 1.65361 3.40423 1.52858 3.52926C1.40355 3.65429 1.33332 3.82387 1.33333 4.00069L2 4.00065ZM2.00052 4.00065V4.66732C2.25679 4.66732 2.49038 4.52042 2.60139 4.28944C2.7124 4.05847 2.68119 3.7843 2.5211 3.58419L2.00052 4.00065ZM2 4H1.33333C1.33333 4.15138 1.38485 4.29825 1.47942 4.41646L2 4ZM3.33333 1.33333C2.22876 1.33333 1.33333 2.22876 1.33333 3.33333H2.66667C2.66667 2.96514 2.96514 2.66667 3.33333 2.66667V1.33333ZM5.51949 1.33333H3.33333V2.66667H5.51949V1.33333ZM6.78441 2.24503C6.60292 1.70057 6.0934 1.33333 5.51949 1.33333V2.66667V2.66667L6.78441 2.24503ZM7.78295 5.24066L6.7844 2.24503L5.51949 2.66667L6.51804 5.66229L7.78295 5.24066ZM7.11432 6.85486C7.7078 6.55812 7.99277 5.87014 7.78295 5.24066L6.51804 5.66229V5.66229L7.11432 6.85486ZM5.60948 7.60728L7.11432 6.85486L6.51804 5.66229L5.0132 6.41471L5.60948 7.60728ZM9.26302 10.0809C7.78142 9.41291 6.58709 8.21858 5.91909 6.73698L4.70359 7.28501C5.50524 9.06305 6.93695 10.4948 8.71499 11.2964L9.26302 10.0809ZM9.14514 8.88568L8.39272 10.3905L9.58529 10.9868L10.3377 9.48196L9.14514 8.88568ZM10.7593 8.21705C10.1299 8.00723 9.44188 8.2922 9.14514 8.88568L10.3377 9.48197V9.48196L10.7593 8.21705ZM13.755 9.2156L10.7593 8.21705L10.3377 9.48196L13.3333 10.4805L13.755 9.2156ZM14.6667 10.4805C14.6667 9.9066 14.2994 9.39708 13.755 9.2156L13.3333 10.4805H13.3333H14.6667ZM14.6667 12.6667V10.4805H13.3333V12.6667H14.6667ZM12.6667 14.6667C13.7712 14.6667 14.6667 13.7712 14.6667 12.6667H13.3333C13.3333 13.0349 13.0349 13.3333 12.6667 13.3333V14.6667ZM12 14.6667H12.6667V13.3333H12V14.6667ZM1.33333 4.00069C1.33371 9.89141 6.10919 14.6667 12 14.6667V13.3333C6.84554 13.3333 2.66699 9.15499 2.66667 4.00061L1.33333 4.00069ZM2.00052 3.33398H2V4.66732H2.00052V3.33398ZM1.47942 4.41646L1.47994 4.41711L2.5211 3.58419L2.52058 3.58354L1.47942 4.41646ZM1.33333 3.33333V4H2.66667V3.33333H1.33333Z" fill="#9CA3AF" />
                                                                    </svg>
                                                                    {feedback.phoneNumber}
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                )
                                            }
                                            <div className={s.contentAndTime}>
                                                <div className={s.content}>{feedback.feedbackContent}</div>
                                            </div>
                                        </div>
                                        <div className={s.starNDate}>
                                            <div className={s.score}><Star yellow width={80} gap={0.2} value={feedback.overallScore} /></div>
                                            <div className={s.time}>{getTimeStr(dayjs(feedback?.answerAt), 'MMMM D, YYYY')}</div>
                                        </div>
                                    </div>
                                    <div className={s.right}>
                                        <Button
                                            onClick={() => {
                                                if (!canReply) {
                                                    setReplyUpgradeTipModalOpen(true);
                                                    return;
                                                }
                                                setCurrentFeedback(feedback);
                                                setShowReply(true);
                                            }}
                                            className={`${replyButtonEnabled ? s.btn : s.btnDisabled}`}
                                            disabled={!replyButtonEnabled}
                                        >Reply
                                        </Button>
                                    </div>
                                </div>
                            );
                        })
                    }
                    <div className={s.paginationBox}>
                        <Pagination current={feedbackListPage} total={totalFeedback} pageSize={feedbackListPageSize} onChange={handlePaginationChange} />
                    </div>
                </Spin>

            </div>
            <ReplyFeedbackModal
                show={showReply}
                item={currentFeedback}
                onSuccess={() => {
                    setShowReply(false);
                }}
                onCannel={() => {
                    setShowReply(false);
                }}
            />
        </div>
    );
};

export default Overview;
